import dayjs from 'dayjs';
import { capitalize } from 'lodash-es';
import { RRule } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { i18next } from '@frontend/i18n';
import { ordinalSuffix } from '@frontend/string';
import { DayMapKey, daysStringMap, getWeekdayFromByWeekDay, weekNumberString } from '../../../../rrule-helper';
import { AvailabilityRecurrenceTypes, AvailabilityRuleFormValues } from '../types';

const { ISO_TIME, US_DATE } = SchedulerV3.Helpers.DATETIME_FORMATS;

export type PreviewPatternFormValues = Pick<AvailabilityRuleFormValues, 'startDate' | 'endDate'> & {
  startTime?: string | null;
  endTime?: string | null;
};

type GetPreviewPatternDescriptionParams<T extends PreviewPatternFormValues> = {
  recurrenceType: AvailabilityRecurrenceTypes | undefined;
  repeatFrequencyType: string | undefined;
  rRule: RRule | undefined;
  formValues: T;
  timezone: string | undefined;
};

const weekdayOptions = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export const getPreviewPatternDescription = <T extends PreviewPatternFormValues>({
  recurrenceType,
  repeatFrequencyType,
  rRule: rRuleProps,
  formValues,
  timezone,
}: GetPreviewPatternDescriptionParams<T>) => {
  if (recurrenceType !== 'CUSTOM' || !rRuleProps || !rRuleProps.options.interval) return '';

  const { startDate, endDate, startTime, endTime } = formValues;
  if (!startDate) return '';

  const getDateWithOptionalTime = (date: string, time?: string | null) => {
    const format = time ? `${US_DATE} ${ISO_TIME}` : US_DATE;
    const value = time ? `${date} ${time}` : date;

    return dayjs(value, format).tz(timezone, true).toDate();
  };

  const dtstartRule = getDateWithOptionalTime(startDate, startTime);
  const untilRule = endDate ? getDateWithOptionalTime(endDate, endTime) : null;

  const rruleOptions = {
    dtstart: dtstartRule,
    until: untilRule,
    tzid: timezone ?? undefined,
  };
  const rRule = new RRule({ ...rruleOptions, ...rRuleProps.origOptions });
  const frequency = rRule.options.interval;
  let patternPrefix = '';
  let patternSuffix = '';
  let nextOccurrences = '';
  const displayCount = repeatFrequencyType === 'days' ? 3 : 6;
  const calculatedOccurrences = rRule.all((_, index) => index < displayCount + 1);
  const occurrences = calculatedOccurrences.slice(0, displayCount);
  const firstOccurrence = occurrences[0];
  const hasMoreOccurrences = (calculatedOccurrences.length ?? 0) > displayCount;
  const terminator = hasMoreOccurrences ? '...' : '.';

  if (repeatFrequencyType === 'days') {
    patternPrefix =
      frequency === 1
        ? i18next.t('everyday', { ns: 'schedule-settings' })
        : i18next.t('every {{frequency}} days', { frequency, ns: 'schedule-settings' });
    nextOccurrences = occurrences.map((date) => dayjs(date).tz(timezone).format('MMM D (ddd)')).join(', ');
  } else if (repeatFrequencyType === 'weeks') {
    patternPrefix =
      frequency === 1
        ? i18next.t('every week', { ns: 'schedule-settings' })
        : i18next.t('every {{frequency}} weeks', { frequency, ns: 'schedule-settings' });
    const weekList = rRule.options.byweekday?.map((weekday: number) => weekdayOptions[weekday]).join(', ') ?? '';
    patternSuffix = weekList ? i18next.t('on {{weekList}}', { weekList, ns: 'schedule-settings' }) : '';
    nextOccurrences = occurrences.map((date) => dayjs(date).tz(timezone).format('MMM D')).join(', ');
  } else if (repeatFrequencyType === 'months') {
    patternPrefix =
      frequency === 1
        ? i18next.t('every month', { ns: 'schedule-settings' })
        : i18next.t('every {{frequency}} months', { frequency, ns: 'schedule-settings' });
    const byweekday = rRule.origOptions.byweekday;
    const byweekdayList = Array.isArray(byweekday) ? byweekday : byweekday ? [byweekday] : [];
    const firstByWeekday = byweekdayList.length > 0 ? byweekdayList[0] : undefined;

    if (byweekday) {
      const [weekday, weekNumber] = getWeekdayFromByWeekDay(firstByWeekday);
      const weekNumString = weekNumber ? weekNumberString[weekNumber] : '';
      const weekdayString = weekday ? daysStringMap[weekday.toString() as DayMapKey] : '';
      patternSuffix =
        weekday && weekNumber
          ? i18next.t('on {{n}} {{day}}', {
              n: capitalize(weekNumString),
              day: capitalize(weekdayString),
              ns: 'schedule-settings',
            })
          : '';
    } else {
      const startingDay = firstOccurrence ? ordinalSuffix(dayjs(firstOccurrence).tz(timezone).date()) : '';
      patternSuffix = startingDay
        ? i18next.t('starting on the {{day}}', { day: startingDay, ns: 'schedule-settings' })
        : '';
    }

    nextOccurrences = occurrences.map((date) => dayjs(date).tz(timezone).format('MMM D')).join(', ');
  }

  const pattern = (patternPrefix + ' ' + patternSuffix).trim();
  const nextOccurrencesText = nextOccurrences
    ? ` ${i18next.t('Next Occurrences', {
        ns: 'schedule-settings',
      })}: ${nextOccurrences}${terminator}`
    : '';

  return patternPrefix
    ? `${i18next.t('Availability', { ns: 'schedule-settings' })} ${pattern}.${nextOccurrencesText}`
    : null;
};
